/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */



import * as Dtos from '../../api/Neon.Dtos';









import Alert from '@material-ui/lab/Alert';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';

import AlertTitle from '@material-ui/lab/AlertTitle';

import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle';

import { faTimesCircle } from '@fortawesome/pro-duotone-svg-icons/faTimesCircle';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IOpmsErrorSummaryProps {
    validationErrors?: Dtos.IValidationError[] | null;
    simpleMessage?: boolean
}


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    error: {
        display: 'flex'
    },
    code: {
        whiteSpace: 'nowrap',
        padding: theme.spacing(1, 1, 1, 0),
        fontWeight: 'bold'
    },
    message: {
        padding: theme.spacing(1, 0, 1, 1)
    }
}));


const useAlertStyles = makeStyles<Theme>(theme => ({
    message: {
        minWidth: '0%'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const OpmsErrorSummary: React.FunctionComponent<IOpmsErrorSummaryProps> = ({
    validationErrors,
    simpleMessage,
    children
}) => {
    const classes = useStyles();

    const notes = React.useMemo(() => validationErrors?.filter(e => e.type === Dtos.ErrorType.Warning), [validationErrors]);

    const eligibilities = React.useMemo(() => validationErrors?.filter(e => e.type === Dtos.ErrorType.Ineligible), [validationErrors]);

    const errors = React.useMemo(() => validationErrors?.filter(e => e.type === Dtos.ErrorType.Normal), [validationErrors]);

    const stratifications = React.useMemo(() => validationErrors?.filter(e => e.type === Dtos.ErrorType.StratificationFailure), [validationErrors]);

    const criticals = React.useMemo(() => validationErrors?.filter(e => e.type === Dtos.ErrorType.Critical), [validationErrors]);

    const alertClasses = useAlertStyles();

    return (
        <>
            {
                notes && notes.length > 0 && (
                    <Alert
                        icon={<FontAwesomeIcon icon={faInfoCircle} fixedWidth />}
                        severity="info"
                        classes={alertClasses}
                    >
                        <AlertTitle>
                            Note
                        </AlertTitle>
                        {
                            notes.map(note => {
                                return (
                                    <div
                                        className={classes.error}
                                        key={note.code}
                                    >
                                        <div
                                            className={classes.code}
                                        >
                                            {note.code}
                                        </div>
                                        <div
                                            className={classes.message}
                                        >
                                            {simpleMessage === true ? note.message : note.detailedMessage}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </Alert>
                )
            }
            {
                eligibilities && eligibilities.length > 0 && (
                    <Alert
                        icon={<FontAwesomeIcon icon={faExclamationCircle} fixedWidth />}
                        severity="warning"
                        classes={alertClasses}
                    >
                        <AlertTitle>
                            Eligibility
                        </AlertTitle>
                        {
                            eligibilities.map(eligibility => {
                                return (
                                    <div
                                        className={classes.error}
                                        key={eligibility.code}
                                    >
                                        <div
                                            className={classes.code}
                                        >
                                            {eligibility.code}
                                        </div>
                                        <div
                                            className={classes.message}
                                        >
                                            {simpleMessage === true ? eligibility.message : eligibility.detailedMessage}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </Alert>
                )
            }
            {
                errors && errors.length > 0 && (
                    <Alert
                        icon={<FontAwesomeIcon icon={faTimesCircle} fixedWidth />}
                        severity="error"
                        classes={alertClasses}
                    >
                        <AlertTitle>
                            Error
                        </AlertTitle>
                        {
                            errors.map(e => {
                                return (
                                    <div
                                        className={classes.error}
                                        key={e.code}
                                    >
                                        <div
                                            className={classes.code}
                                        >
                                            {e.code}
                                        </div>
                                        <div
                                            className={classes.message}
                                        >
                                            {simpleMessage === true ? e.message : e.detailedMessage}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </Alert>
                )
            }
            {
                stratifications && stratifications.length > 0 && (
                    <Alert
                        icon={<FontAwesomeIcon icon={faExclamationCircle} fixedWidth />}
                        severity="warning"
                        variant="filled"
                        classes={alertClasses}
                    >
                        <AlertTitle>
                            Stratification
                        </AlertTitle>
                        {
                            stratifications.map(stratification => {
                                return (
                                    <div
                                        className={classes.error}
                                        key={stratification.code}
                                    >
                                        <div
                                            className={classes.code}
                                        >
                                            {stratification.code}
                                        </div>
                                        <div
                                            className={classes.message}
                                        >
                                            {simpleMessage === true ? stratification.message : stratification.detailedMessage}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </Alert>
                )
            }
            {
                criticals && criticals.length > 0 && (
                    <Alert
                        icon={<FontAwesomeIcon icon={faTimesCircle} fixedWidth />}
                        severity="error"
                        variant="filled"
                        classes={alertClasses}
                    >
                        <AlertTitle>
                            Critical
                        </AlertTitle>
                        {
                            criticals.map(critical => {
                                return (
                                    <div
                                        className={classes.error}
                                        key={critical.code}
                                    >
                                        <div
                                            className={classes.code}
                                        >
                                            {critical.code}
                                        </div>
                                        <div
                                            className={classes.message}
                                        >
                                            {simpleMessage === true ? critical.message : critical.detailedMessage}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </Alert>
                )
            }
        </>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsErrorSummary;
